/**
 * The (i) beside a figure: how it is worked out, on hover or focus.
 *
 * Drawn at a fixed position rather than inside its parent, because most
 * figures sit in a scrolling table or a card that clips what overflows it.
 * Inside a card that opens a breakdown, pressing the (i) explains rather
 * than opening — the click stops here.
 */

import { useId, useRef, useState, type MouseEvent, type KeyboardEvent } from "react";

export function Info({ text, label = "How this is calculated" }: { text?: string | null; label?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ x: number; y: number; above: boolean } | null>(null);
  const id = useId();
  if (!text) return null;

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const width = 280;
    const x = Math.min(Math.max(r.left + r.width / 2 - width / 2, 8), window.innerWidth - width - 8);
    const above = r.bottom + 140 > window.innerHeight;
    setAt({ x, y: above ? r.top - 6 : r.bottom + 6, above });
  };
  const hide = () => setAt(null);
  const stop = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    if ("key" in e && e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (at) hide();
    else show();
  };

  return (
    <span
      ref={ref}
      className="info"
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-describedby={at ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={stop}
      onKeyDown={stop}
    >
      i
      {at && (
        <span
          id={id}
          role="tooltip"
          className={`info__tip${at.above ? " info__tip--above" : ""}`}
          style={{ left: at.x, top: at.y }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
